// 7-pill assignment widget.
//
// Replaces the legacy 3-icon assignee row on the
// deal-detail header. compact = true renders small read-only pills with no
// names (no caller sets it); compact = false is the deal-detail page and the
// Underwriting tab (clickable, pops over the assignment editor).
//
// Pill state mapping:
//   Unassigned     → outlined, muted text.
//   Assigned       → filled gold, onyx text.
//   NotApplicable  → strikethrough, muted red, no fill.
//
// Pre-Underwriting visibility: A2/A3/A4/B2 pills render
// disabled with "Assignable once the deal reaches Underwriting" tooltip
// when Deal.Stage is earlier than Underwriting.
//
// In full mode an assigned pill names its holder ("Assigned to …") in its
// tooltip and popover, resolved through the internal-user labels.
//
// readOnly renders full-size pills with no popover, for a deal whose writes the
// server refuses (Closed, a ratified decline, or Withdrawn / stage-Declined
// for anyone but ADMIN — see dealMenuRules.canWriteToDeal).

import { useMemo } from "react";
import { DealAssignmentPopover } from "./DealAssignmentPopover";
import { fallbackUserLabel, useInternalUserLabels } from "@/api/internalUsers";
import {
  DEAL_ROLE_CODES,
  DEAL_ROLE_LABEL,
  type DealAssignmentDto,
  type DealAssignmentStatus,
  type DealRoleCode,
} from "@/api/dealFlow/types";
import { useDealAssignments } from "@/hooks/dealFlow/useDealFlow";
import type { DealPipelineStage } from "@/types/deals";

interface DealAssignmentPillsProps {
  dealId: string;
  /** Current deal stage — gates A2/A3/A4/B2 pills before Underwriting. */
  dealStage: DealPipelineStage;
  /** When true: smaller pills, no popover. No caller sets it.
   *  Default false (full mode on deal-detail header). */
  compact?: boolean;
  /** When true: full-size pills with names but no popover, because the
   *  server refuses assignment writes on this deal. Default false. */
  readOnly?: boolean;
}

const PRE_UW_GATED: ReadonlySet<DealRoleCode> = new Set(["A2", "A3", "A4", "B2"]);

function pillsByRole(
  assignments: DealAssignmentDto[] | undefined,
): Record<DealRoleCode, DealAssignmentDto | undefined> {
  const out: Partial<Record<DealRoleCode, DealAssignmentDto>> = {};
  for (const a of assignments ?? []) {
    out[a.roleCode as DealRoleCode] = a;
  }
  // Force-cast: any missing roles render as Unassigned via the fallback
  // logic in <Pill>.
  return out as Record<DealRoleCode, DealAssignmentDto | undefined>;
}

export function DealAssignmentPills({
  dealId,
  dealStage,
  compact = false,
  readOnly = false,
}: DealAssignmentPillsProps) {
  const { data: assignments, isLoading } = useDealAssignments(dealId);

  const byRole = useMemo(() => pillsByRole(assignments), [assignments]);
  // Compact pills show no names, so they resolve none.
  const assigneeIds = compact
    ? []
    : (assignments ?? [])
        .filter((a) => a.status === "Assigned" && a.assignedUserId)
        .map((a) => a.assignedUserId as string);
  const { lookup } = useInternalUserLabels(assigneeIds);
  const assigneeName = (a: DealAssignmentDto | undefined): string | null => {
    if (compact || a?.status !== "Assigned" || !a.assignedUserId) return null;
    return lookup(a.assignedUserId)?.displayName?.trim() || fallbackUserLabel(a.assignedUserId);
  };
  const stageGated = (role: DealRoleCode): boolean =>
    PRE_UW_GATED.has(role) &&
    dealStage !== "Underwriting" &&
    dealStage !== "Closing" &&
    dealStage !== "Closed";

  if (isLoading) {
    return (
      <div
        className="flex items-center gap-space-1"
        data-testid="deal-assignment-pills-skeleton"
      >
        {DEAL_ROLE_CODES.map((r) => (
          <span
            key={r}
            className={`inline-flex items-center justify-center rounded-pill border border-border-light bg-white-warm animate-pulse ${
              compact ? "h-5 w-8 text-[10px]" : "h-7 w-10 text-caption"
            }`}
            aria-hidden
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-space-1"
      role="group"
      aria-label="Deal assignments"
      data-testid="deal-assignment-pills"
    >
      {DEAL_ROLE_CODES.map((role) => (
        <Pill
          key={role}
          dealId={dealId}
          role={role}
          assignment={byRole[role]}
          assigneeName={assigneeName(byRole[role])}
          compact={compact}
          readOnly={readOnly}
          stageGated={stageGated(role)}
        />
      ))}
    </div>
  );
}

interface PillProps {
  dealId: string;
  role: DealRoleCode;
  assignment: DealAssignmentDto | undefined;
  /** The holder's name, in full mode when the pill is Assigned. */
  assigneeName: string | null;
  compact: boolean;
  readOnly: boolean;
  stageGated: boolean;
}

function Pill({ dealId, role, assignment, assigneeName, compact, readOnly, stageGated }: PillProps) {
  const status: DealAssignmentStatus = assignment?.status ?? "Unassigned";

  const sizeClass = compact ? "h-5 px-space-1 text-[10px]" : "h-7 px-space-2 text-caption";
  const stateClass = stateClassName(status, stageGated);
  const baseClass = `inline-flex items-center justify-center font-display font-bold uppercase tracking-label rounded-pill border transition-colors ${sizeClass} ${stateClass}`;

  const titleParts: string[] = [`${role} — ${DEAL_ROLE_LABEL[role]}`];
  if (status === "Assigned") {
    titleParts.push(assigneeName ? `Assigned to ${assigneeName}` : "Assigned");
  } else if (status === "NotApplicable") {
    titleParts.push("Marked Not Applicable for this deal");
  } else {
    titleParts.push(
      stageGated
        ? "Assignable once the deal reaches Underwriting"
        : readOnly
          ? "Unassigned"
          : "Unassigned · click to assign",
    );
  }
  const title = titleParts.join(" · ");

  if (compact || readOnly || stageGated) {
    // Read-only mode (compact = small pills; readOnly = a locked deal;
    // stage-gated = pre-Underwriting).
    return (
      <span
        className={baseClass}
        title={title}
        aria-label={title}
        data-testid={`pill-${role}`}
        data-status={status}
        data-stage-gated={stageGated ? "true" : undefined}
      >
        {role}
      </span>
    );
  }

  return (
    <DealAssignmentPopover
      dealId={dealId}
      role={role}
      assignment={assignment}
      assigneeName={assigneeName}
      trigger={
        <button
          type="button"
          className={`${baseClass} hover:ring-2 hover:ring-gold/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold`}
          title={title}
          aria-label={title}
          data-testid={`pill-${role}`}
          data-status={status}
        >
          {role}
        </button>
      }
    />
  );
}

function stateClassName(status: DealAssignmentStatus, stageGated: boolean): string {
  if (stageGated) {
    return "border-border-light bg-white-warm text-gray-mid opacity-60 cursor-not-allowed";
  }
  switch (status) {
    case "Assigned":
      return "bg-gold text-onyx border-gold";
    case "NotApplicable":
      return "border-status-error/40 text-status-error line-through";
    case "Unassigned":
    default:
      return "border-border-light text-gray-mid bg-white-pure";
  }
}
